import { useMemo, useRef, useState } from 'react';
import AssistantComposer from './AssistantComposer';
import { MessageSquareText } from 'lucide-react';
import { SectionCard } from 'components/SectionCard';
import { Skeleton } from '@/ui/design-system/components/ui/skeleton';
import { SpateAiIcon } from '@/ui/design-system/components/ui/spate-ai-icon';
import UseCasePills from './UseCasePills';
import { formatRelativeTime } from './sessions/utils';
import { mixpanelTrack } from 'utils/app/configureMixpanel';
import { useAssistantContext } from './AssistantProvider';
import { useLocation } from 'react-router-dom';
import { useSessionIndex } from './sessions/useSessionQueries';
import { views } from '@/copy';

const RECENT_CHATS_LIMIT = 2;

function HomeCommandCenter() {
    const { pathname } = useLocation();
    const [query, setQuery] = useState('');
    const textareaRef = useRef(null);
    const {
        isLoading,
        openPanel,
        sendMessage,
        sessions,
        switchToSession,
        modelTier,
        setModelTier,
    } = useAssistantContext();
    const { status: sessionsStatus } = useSessionIndex();
    const isSessionsLoading = ['idle', 'loading'].includes(sessionsStatus);

    const sortedChats = useMemo(() => {
        return [...(sessions || [])]
            .sort((a, b) => b.updatedAt - a.updatedAt)
            .slice(0, RECENT_CHATS_LIMIT);
    }, [sessions]);
    const visibleRecentChats = sortedChats.slice(0, RECENT_CHATS_LIMIT);

    if (pathname !== '/') {
        return null;
    }

    const resetTextareaHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    };

    const submitPrompt = (prompt, meta = {}) => {
        const trimmedPrompt = prompt.trim();
        if (!trimmedPrompt) return;

        openPanel();
        sendMessage(trimmedPrompt, {
            newChat: true,
            skipPageContext: true,
            source: 'homepage',
            ...meta,
        });
        setQuery('');
        resetTextareaHeight();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submitPrompt(query);
        }
    };

    const handleUseCaseSelect = (item) => {
        mixpanelTrack('AI Assistant Suggestion Clicked', {
            suggestion_text: item.prompt,
            use_case: item.label,
            source: 'home_command_center',
        });
        setQuery(item.prompt);
        requestAnimationFrame(() => {
            const el = textareaRef.current;
            if (!el) return;
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
        });
    };

    return (
        <SectionCard
            id="home-assistant-command-center"
            css="padding: 16px 18px 18px;"
        >
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-center gap-2">
                    <SpateAiIcon size={24} />
                    <p className="m-0 text-lg font-semibold text-foreground">
                        Can I help you with anything?
                    </p>
                </div>
                <AssistantComposer
                    value={query}
                    onValueChange={setQuery}
                    onSubmit={() => submitPrompt(query)}
                    onKeyDown={handleKeyDown}
                    isLoading={isLoading}
                    modelTier={modelTier}
                    setModelTier={setModelTier}
                    textareaRef={textareaRef}
                    placeholder={views.assistant.homePlaceholder}
                    disclosure="none"
                    showFreePreview={false}
                    className="p-0"
                />

                <UseCasePills onSelect={handleUseCaseSelect} />

                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-3">
                        <p className="m-0 text-base font-semibold text-foreground">
                            Recent chats
                        </p>
                        {sessions?.length > visibleRecentChats.length ? (
                            <button
                                type="button"
                                onClick={() => openPanel('history')}
                                className="border-none bg-transparent p-0 text-sm text-muted-foreground transition-colors hover:text-foreground"
                            >
                                Chat history
                            </button>
                        ) : null}
                    </div>

                    {isSessionsLoading ? (
                        <div className="grid grid-cols-2 gap-3 max-[900px]:grid-cols-1">
                            {Array.from({ length: RECENT_CHATS_LIMIT }).map(
                                (_, idx) => (
                                    <div
                                        key={`chat-skeleton-${idx}`}
                                        className="flex h-16 items-center gap-[10px] rounded-[14px] border border-base-card bg-background px-3 py-[11px]"
                                    >
                                        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] bg-muted text-muted-foreground">
                                            <MessageSquareText size={15} />
                                        </span>
                                        <div className="min-w-0 flex flex-col gap-2">
                                            <Skeleton className="h-3.5 w-44 bg-neutral-200" />
                                            <Skeleton className="h-3 w-20 bg-neutral-200" />
                                        </div>
                                    </div>
                                ),
                            )}
                        </div>
                    ) : visibleRecentChats.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3 max-[900px]:grid-cols-1">
                            {visibleRecentChats.map((session) => (
                                <button
                                    type="button"
                                    key={session.id}
                                    onClick={() => {
                                        openPanel();
                                        switchToSession(session.id);
                                    }}
                                    className="flex h-16 items-center gap-[10px] rounded-[14px] border border-base-card bg-background px-3 py-[11px] text-left transition-colors hover:border-muted-foreground hover:bg-[#faf9fc]"
                                >
                                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] bg-muted text-muted-foreground">
                                        <MessageSquareText size={15} />
                                    </span>
                                    <div className="min-w-0 flex flex-col gap-0.5">
                                        <span className="truncate text-sm text-foreground">
                                            {session.label}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {formatRelativeTime(
                                                session.updatedAt,
                                            )}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className="m-0 text-sm text-muted-foreground">
                            No previous chats yet. Start with a suggested action
                            above.
                        </p>
                    )}
                </div>
            </div>
        </SectionCard>
    );
}

export default HomeCommandCenter;
